/**
 * The document model (decision 66; part 04 log, tables B and I): what a chapter means, read from its tree.
 * Two sources with the same model show the writer the same thing, so the canonical form (serialize.ts) is
 * written from the model alone, and canonicalising a source changes none of it.
 *
 * What the model leaves out, because it is how a chapter is written rather than what it says: link reference
 * definitions (their links carry the target), the characters a heading, list or scene break is written with,
 * list item numbers after the first, indentation, and the whitespace at the edges of lines.
 */
import type { SyntaxNode } from '@lezer/common';
import type { ParsedChapter } from '../parser/parse.ts';
import { destinationText, labelKey, rawText, readInline, requireChild, titleText } from './inline.ts';
import type { Definitions, Inline, InlineContext, LinkTarget } from './inline.ts';

/** Where a block sits in the source. */
export interface Extent {
  readonly from: number;
  readonly to: number;
}

/** A paragraph, or indented text read as one (G3). */
export interface Paragraph extends Extent {
  readonly kind: 'paragraph';
  readonly content: readonly Inline[];
}

/** An ATX or setext heading. */
export interface Heading extends Extent {
  readonly kind: 'heading';
  readonly level: 1 | 2 | 3 | 4 | 5 | 6;
  readonly content: readonly Inline[];
}

/** `->text<-`. */
export interface Centered extends Extent {
  readonly kind: 'centered';
  readonly content: readonly Inline[];
}

/** Any thematic break. */
export interface SceneBreak extends Extent {
  readonly kind: 'scene-break';
}

/** A block quote. */
export interface Quote extends Extent {
  readonly kind: 'quote';
  readonly blocks: readonly Block[];
}

/** A bullet or ordered list: each item is the blocks it holds. */
export interface List extends Extent {
  readonly kind: 'list';
  /** The first item's number, or null for a bullet list. */
  readonly start: number | null;
  /** CommonMark's loose list (items or their blocks separated by blank lines), plus N9. */
  readonly loose: boolean;
  readonly items: readonly (readonly Block[])[];
  /** Where each item's content ends: its last block's, or its marker's when it is empty. */
  readonly itemEnds: readonly number[];
}

/** A footnote definition; the label is raw source. */
export interface Footnote extends Extent {
  readonly kind: 'footnote';
  readonly label: string;
  readonly blocks: readonly Block[];
}

/** One block. */
export type Block = Paragraph | Heading | Centered | SceneBreak | Quote | List | Footnote;

/** A chapter's model: its blocks in order. */
export interface ChapterModel {
  readonly blocks: readonly Block[];
}

const HEADING_LEVELS: ReadonlyMap<string, Heading['level']> = new Map([
  ['ATXHeading1', 1],
  ['ATXHeading2', 2],
  ['ATXHeading3', 3],
  ['ATXHeading4', 4],
  ['ATXHeading5', 5],
  ['ATXHeading6', 6],
  ['SetextHeading1', 1],
  ['SetextHeading2', 2],
]);

/** Every link reference definition in the chapter, first of each label first (CommonMark 4.7). */
function definitions(chapter: ParsedChapter): Definitions {
  const found = new Map<string, LinkTarget>();
  const cx: InlineContext = { source: chapter.text, definitions: found };
  const cursor = chapter.tree.cursor();
  do {
    if (cursor.name !== 'LinkReference') continue;
    const node = cursor.node;
    const label = requireChild(node, 'LinkLabel');
    const key = labelKey(rawText(node, label.from + 1, label.to - 1, chapter.text));
    if (key === '' || found.has(key)) continue;
    // A definition always has a destination (`<>` at least); Lezer marks nothing else as one.
    const title = node.getChild('LinkTitle');
    found.set(key, { destination: destinationText(requireChild(node, 'URL'), cx), title: title === null ? null : titleText(title, cx) });
  } while (cursor.next());
  return found;
}

/** The model of a parsed chapter. Pure; linear in the source. */
export function chapterModel(chapter: ParsedChapter): ChapterModel {
  const cx: InlineContext = { source: chapter.text, definitions: definitions(chapter) };
  return { blocks: blocksOf(chapter.tree.topNode, cx, 0) };
}

/**
 * The blocks directly inside a container node; marks and link reference definitions give none. `quotes` is
 * how many block quotes enclose them.
 */
function blocksOf(node: SyntaxNode, cx: InlineContext, quotes: number): Block[] {
  const out: Block[] = [];
  for (let child = node.firstChild; child !== null; child = child.nextSibling) {
    const block = blockOf(child, cx, quotes);
    if (block !== null) out.push(block);
  }
  return out;
}

/** Inline content of a leaf block: it starts and ends a line. */
function content(node: SyntaxNode, from: number, to: number, cx: InlineContext): Inline[] {
  return readInline(node, from, to, cx, true);
}

function blockOf(node: SyntaxNode, cx: InlineContext, quotes: number): Block | null {
  const { from, to } = node;
  switch (node.name) {
    case 'Paragraph':
      return { kind: 'paragraph', from, to, content: content(node, from, to, cx) };
    case 'CenteredBlock': {
      const open = requireChild(node, 'CenterMark');
      const close = requireChild(node, 'CenterMark', 1);
      return { kind: 'centered', from, to, content: content(node, open.to, close.from, cx) };
    }
    case 'SceneBreak':
      return { kind: 'scene-break', from, to };
    case 'Blockquote':
      return { kind: 'quote', from, to, blocks: blocksOf(node, cx, quotes + 1) };
    case 'BulletList':
    case 'OrderedList':
      return list(node, cx, quotes);
    case 'FootnoteDefinition': {
      const label = requireChild(node, 'FootnoteLabel');
      return { kind: 'footnote', from, to, label: cx.source.slice(label.from, label.to), blocks: blocksOf(node, cx, quotes) };
    }
    default:
      return heading(node, cx);
  }
}

/**
 * A heading, or null for a node that is no block (marks, link reference definitions). An ATX heading's
 * content lies between its opening and closing marks; a setext heading's ends with the line before its
 * underline, whose container prefix Lezer leaves out of the tree.
 */
function heading(node: SyntaxNode, cx: InlineContext): Heading | null {
  const level = HEADING_LEVELS.get(node.name);
  if (level === undefined) return null;
  const first = requireChild(node, 'HeaderMark');
  const last = requireChild(node, 'HeaderMark', -1);
  // An ATX heading's content ends at its closing sequence, if it has one (whitespace can follow that).
  const [start, end] = node.name.startsWith('Setext')
    ? [node.from, cx.source.lastIndexOf('\n', first.from)]
    : [first.to, last.from === first.from ? node.to : last.from];
  return { kind: 'heading', from: node.from, to: node.to, level, content: content(node, start, end, cx) };
}

/**
 * Whether a line is blank for a block inside `quotes` block quotes: nothing but spaces and at most that many
 * quote markers. A further `>` belongs to a quote inside the block, and makes the line part of it.
 */
function isBlank(line: string, quotes: number): boolean {
  const marks = line.replace(/[ \t\r]/gu, '');
  return marks.replaceAll('>', '') === '' && marks.length <= quotes;
}

/**
 * Where a block's content ends: its last leaf, or its own marker when it holds nothing. Lezer counts a
 * container's trailing blank quote lines into its node, so its `to` can lie past them.
 */
function contentEnd(block: Block): number {
  switch (block.kind) {
    case 'quote':
    case 'footnote':
      // An empty one ends on its marker's line; any point on that line will do, since the search starts after it.
      return block.blocks.reduce((_, b) => contentEnd(b), block.from);
    case 'list':
      return block.itemEnds.reduce((_, end) => end, block.to);
    case 'paragraph':
    case 'heading':
    case 'centered':
    case 'scene-break':
      return block.to;
  }
}

/** Whether a blank line (at this quote depth) lies between content ending at `end` and a block starting at `next`. */
function blankLineBetween(source: string, end: number, next: number, quotes: number): boolean {
  return source.slice(end, next).split('\n').slice(1, -1).some((line) => isBlank(line, quotes));
}

/** One list item: where its marker ends, where it starts, and its blocks. */
interface ItemParts {
  readonly from: number;
  readonly markEnd: number;
  readonly blocks: Block[];
}

/** Where an item's content ends: its last block's, or its marker's. */
function itemEnd(item: ItemParts): number {
  return item.blocks.reduce((_, b) => contentEnd(b), item.markEnd);
}

/**
 * N9: two blocks no tight list item can hold one after the other, since whatever line keeps them apart is
 * blank or joins them. A block ending open followed by a list starting with an empty item (an empty item
 * can't interrupt a paragraph; dropped link reference definitions emptied it), and two quotes (one `>` line
 * merges them). An open end inside a note or a list counts too.
 */
function inexpressibleTight(previous: Block | null, block: Block): boolean {
  const emptyFirst = block.kind === 'list' && block.items.findIndex((i) => i.length === 0) === 0;
  return (previous !== null && emptyFirst && endsOpen(previous, false)) || (previous?.kind === 'quote' && block.kind === 'quote');
}

/** Whether the last of `blocks` ends open (see {@link endsOpen}). */
function lastEndsOpen(blocks: readonly Block[], quotes: boolean): boolean {
  return blocks.some((b, i) => i === blocks.length - 1 && endsOpen(b, quotes));
}

/**
 * Whether a line after the block could join its last paragraph lazily (CommonMark 5.1): it ends in a
 * paragraph or centered block, directly or as the last block of a note, of a list's last item, or, when
 * `quotes`, of a quote. N9 leaves quotes out, since a `>` line closes them (B18); the serializer counts them
 * to know when to write that line.
 */
export function endsOpen(block: Block, quotes: boolean): boolean {
  switch (block.kind) {
    case 'paragraph':
    case 'centered':
      return true;
    case 'quote':
      return quotes && lastEndsOpen(block.blocks, quotes);
    case 'footnote':
      return lastEndsOpen(block.blocks, quotes);
    case 'list':
      return block.items.some((item, k) => k === block.items.length - 1 && lastEndsOpen(item, quotes));
    case 'heading':
    case 'scene-break':
      return false;
  }
}

/**
 * Whether a list is loose (CommonMark 5.3, with link reference definitions not counted as blocks, as cmark
 * drops them): two of its items, or two blocks of one item, have a blank line between them; or an item holds
 * two blocks no tight item can write in a row (N9).
 */
function isLoose(items: readonly ItemParts[], source: string, quotes: number): boolean {
  return items.some((item, k) => {
    const next = items.at(k + 1);
    const end = itemEnd(item);
    if (next !== undefined && blankLineBetween(source, end, next.from, quotes)) return true;
    let previous: Block | null = null;
    for (const block of item.blocks) {
      if (previous !== null && blankLineBetween(source, contentEnd(previous), block.from, quotes)) return true;
      if (inexpressibleTight(previous, block)) return true;
      previous = block;
    }
    return false;
  });
}

function list(node: SyntaxNode, cx: InlineContext, quotes: number): List {
  const items = node.getChildren('ListItem').map((item): ItemParts => ({
    from: item.from,
    markEnd: requireChild(item, 'ListMark').to,
    blocks: blocksOf(item, cx, quotes),
  }));
  const firstMark = requireChild(requireChild(node, 'ListItem'), 'ListMark');
  const start = node.name === 'OrderedList' ? Number.parseInt(cx.source.slice(firstMark.from, firstMark.to), 10) : null;
  return {
    kind: 'list',
    from: node.from,
    to: node.to,
    start,
    loose: isLoose(items, cx.source, quotes),
    items: items.map((i) => i.blocks),
    itemEnds: items.map(itemEnd),
  };
}
